import {
  Autocomplete,
  TextField,
  CircularProgress,
  createFilterOptions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useField, useFormikContext } from "formik";
import { FC, useEffect, useState, Fragment } from "react";
import { useAppDispatch } from "../../app/hooks";
import { useGetListQuery } from "../../features/api/Api";
import {
  selectCustomerAddressLocation,
  setAddressLocation,
} from "../../features/customer/customerSlice";
import { IOption } from "../../interfaces/Utils";
import { isExists, locale, params } from "../../utils";
import { useAppSelector } from "../../app/hooks";
import { useSearchDebounce } from "../../hooks";

interface IReferenceSelect {
  name: string;
  label: string;
  reference: string;
  optionName: string;
  placeholder?: string;
  disabled?: boolean;
  optionId?: string;
  component?: any;
  skip?: boolean;
  secondOptionName?: string;
  localePrefix?: string;
  secondField?: any;
  query?: any;
}

const CssTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#0063cc",
  },
  "& label.Mui-disabled": {
    color:
      localStorage.getItem("dark") === "true"
        ? "rgba(255, 255, 255, 0.38) !important"
        : "rgba(0, 0, 0, 0.38)",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#0063cc",
  },
  ".MuiOutlinedInput-input.Mui-disabled": {
    textFillColor:
      localStorage.getItem("dark") === "true"
        ? "rgba(255, 255, 255, 0.38) !important"
        : "rgba(0, 0, 0, 0.38)",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#e5e5e5",
    },
    "&.Mui-disabled": {
      color:
        localStorage.getItem("dark") === "true"
          ? "rgba(255, 255, 255, 0.38)"
          : "rgba(0, 0, 0, 0.38)",
      fieldset: {
        borderColor:
          localStorage.getItem("dark") === "true"
            ? "rgba(255, 255, 255, 0.38)"
            : "rgba(0, 0, 0, 0.38)",
      },
    },
    "&:hover fieldset": {
      borderColor: "#0063cc",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#0063cc",
    },
  },
});

function setFilter(
  query: any,
  search: string,
  id: string | number | null = null
) {
  let customQuery = {};
  if (query) {
    customQuery = { ...query };
  }

  if (search) {
    customQuery = { ...customQuery, name: search };
  }

  if (id) {
    customQuery = { ...customQuery, id };
  }

  if (Object.keys(customQuery).length > 0) {
    return customQuery;
  } else {
    return {};
  }
}

function setInitials(initialValues: any, name: string) {
  if (!name) {
    return null;
  }

  const nameList = name.split(".");

  if (nameList.length === 2) {
    return initialValues[nameList[0]][nameList[1]];
  }

  return initialValues[name];
}

function clearableFields(name: string): string[] {
  const clearFields = [
    "city",
    "town",
    "townShip",
    "village",
    "district",
    "street",
    "building",
    "door",
  ];

  const locationIndex = clearFields.indexOf(name);
  let fields = [...clearFields];
  const filteredFields = fields.splice(locationIndex + 1, fields.length);

  return filteredFields;
}

function clearChildField(name: string, setFieldValue: any) {
  if (name === "city") {
    const clearFields = clearableFields("city");
    clearFields.map((item: string) => setFieldValue(item, "", false));
  } else if (name === "town") {
    const clearFields = clearableFields("town");
    clearFields.map((item: string) => setFieldValue(item, "", false));
  } else if (name === "townShip") {
    const clearFields = clearableFields("townShip");
    clearFields.map((item: string) => setFieldValue(item, "", false));
  } else if (name === "village") {
    const clearFields = clearableFields("village");
    clearFields.map((item: string) => setFieldValue(item, "", false));
  } else if (name === "district") {
    const clearFields = clearableFields("district");
    clearFields.map((item: string) => setFieldValue(item, "", false));
  } else if (name === "street") {
    const clearFields = clearableFields("street");
    clearFields.map((item: string) => setFieldValue(item, "", false));
  } else if (name === "building") {
    const clearFields = clearableFields("building");
    clearFields.map((item: string) => setFieldValue(item, "", false));
  }
}

const AddressReferenceDropDown: FC<IReferenceSelect> = (props) => {
  const {
    name,
    label,
    reference,
    optionName,
    secondOptionName,
    optionId,
    placeholder,
    disabled,
    skip,
    localePrefix,
    secondField,
    query,
  } = props;
  const { initialValues, setFieldValue }: any = useFormikContext();
  const [field, meta] = useField(name);
  const [options, setOptions] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const addressLocations = useAppSelector(selectCustomerAddressLocation);
  const [search, setSearchQuery] = useSearchDebounce();
  const [skipCheck, setSkipCheck] = useState(true);
  const [fetchParams, setFetchParams] = useState({
    resource: reference,
    params: {
      ...params,
      pagination: { pageNumber: 0, size: 100 },
      filter: setFilter(query, search, setInitials(initialValues, name)),
    },
  });

  const { data: currentData, isFetching } = useGetListQuery<any>(fetchParams, {
    skip: skipCheck,
    refetchOnMountOrArgChange: true,
  });

  function searchQueryHandler(event: any) {
    setSearchQuery(event.target.value);
  }

  useEffect(() => {
    if (!skip) {
      setSkipCheck(false);
    } else {
      setSkipCheck(true);
    }
  }, [skip]);

  useEffect(() => {
    if (search) {
      setFetchParams((prev: any) => ({
        ...prev,
        params: {
          ...prev.params,
          filter: setFilter(query, search, ""),
        },
      }));
    }

    if (query) {
      setFetchParams((prev: any) => ({
        ...prev,
        params: {
          ...prev.params,
          filter: setFilter(query, search),
        },
      }));
    }
  }, [search, setFetchParams, query]);

  useEffect(() => {
    if (
      isExists(secondField?.name) &&
      isExists(currentData?.data) &&
      isExists(field?.value)
    ) {
      const { name, value }: any = secondField;
      const secondFieldValue = currentData.data.find(
        (item: any) => item.turkTelekomId === field.value.value
      )?.[value];
      if (isExists(secondField)) {
        setFieldValue(name, secondFieldValue, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field?.value?.value, currentData]);

  useEffect(() => {
    if (isExists(currentData?.data)) {
      let records: IOption[] = [];
      currentData.data.map((item: any) =>
        records.push({
          value: optionId ? item[optionId] : item.id,
          label: secondOptionName
            ? item[optionName] + " - " + item[secondOptionName]
            : item[optionName],
        })
      );
      const listIndex = addressLocations.findIndex(
        (item: any) => item.name === field.name
      );

      if (listIndex === -1) {
        dispatch(
          setAddressLocation([
            ...addressLocations,
            { name: field.name, data: currentData.data },
          ])
        );
      } else {
        let adresses: any = [];
        addressLocations.forEach((item: any, index: number) => {
          if (index === listIndex) {
            adresses.push({ name: field.name, data: currentData.data });
          } else {
            adresses.push(item);
          }
        });

        dispatch(setAddressLocation(adresses));
      }

      records = locale(records, localePrefix);

      setOptions(records);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  function changeHandler(event: any, newValue: any) {
    if (isExists(newValue)) {
      setFieldValue(field.name, newValue);
    } else {
      clearChildField(name, setFieldValue);
      setFieldValue(field.name, "");
      setFetchParams((prev: any) => ({
        ...prev,
        params: { ...prev.params, filter: setFilter(query, "", "") },
      }));
    }
  }

  /* const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option: any) => {
      let searchTerm = option.label;

      searchTerm = searchTerm.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

      let filteredArray = options.filter((item: IOption) => {
        let normalizedItem = item.label
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        return normalizedItem.toLowerCase().includes(searchTerm.toLowerCase());
      });
      console.log({ filteredArray, searchTerm });
      return "test";
    },
  }); */

  const TurkishSensitiveSearch = (searchTerm: any, item: any) => {
    const normalizedSearch = searchTerm
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();

    const normalizedItem = item.label
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();

    return normalizedItem
      .toLowerCase()
      .includes(normalizedSearch.toLowerCase());
  };

  return (
    <Autocomplete
      isOptionEqualToValue={(option, value) => true}
      value={
        isExists(field.value) && options.length > 0
          ? options.find((option) => option.value === field.value.value)
          : ""
      }
      onChange={changeHandler}
      id={field.name}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      //filterOptions={filterOptions}
      //getOptionLabel={(option) => option.label}
      filterOptions={(options, { inputValue }) => {
        return options.filter((option) =>
          TurkishSensitiveSearch(inputValue, option)
        );
      }}
      renderInput={(params) => (
        <CssTextField
          {...params}
          label={label}
          error={Boolean(meta.error)}
          helperText={meta.error && meta.touched && meta.error}
          //onChange={searchQueryHandler}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {isFetching && <CircularProgress color="primary" size={20} />}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default AddressReferenceDropDown;
